import { defineMessages, MessageDescriptor } from "react-intl";
import { define } from "cooky-cutter";
import { faker } from "@faker-js/faker";

import { AssetId } from "@/features/assets/types";
import { TripId } from "@/features/trips/types";
import {
  Category,
  Location,
  locationFactory,
  Point,
  pointAndRadiusFactory,
} from "@/features/types";

import { ApiPaginatedResponse } from "@/utils/remote/types";

import { DeviceType } from "../alerts/types";

export type DevicesResponse = ApiPaginatedResponse & { devices: Device[] };

export type ReadersResponse = ApiPaginatedResponse & { readers: Reader[] };

export enum BatteryState {
  BATTERY_STATE_UNSPECIFIED = "Unspecified",
  OK = "Ok",
  LOW = "Low",
  EMPTY = "Empty",
}

export type BatteryStateKey = keyof typeof BatteryState;

export enum MapFeatureType {
  GEO_FENCE = "Geofence",
  ANCHOR_POINT = "Anchor Point",
  PLACE = "Place",
}

export enum UserRole {
  USER = "User",
  MANAGER = "Manager",
}

export type UserRoleKey = keyof typeof UserRole;
export const UserRoleKeys = Object.keys(UserRole) as (keyof typeof UserRole)[];

export enum UserStatus {
  ACTIVE = "Active",
  DEACTIVATED = "Deactivated",
}

export type UserStatusKey = keyof typeof UserStatus;
export const UserStatusKeys = Object.keys(
  UserStatus,
) as (keyof typeof UserStatus)[];

export const batteryMessages: Record<
  keyof typeof BatteryState,
  MessageDescriptor
> = defineMessages({
  BATTERY_STATE_UNSPECIFIED: {
    defaultMessage: "Unspecified",
    description:
      "Battery state shown when sensor battery status cannot be determined",
  },
  OK: {
    defaultMessage: "Ok",
    description: "Battery state indicating sensor has sufficient power",
  },
  LOW: {
    defaultMessage: "Low",
    description: "Battery state warning that sensor power is running low",
  },
  EMPTY: {
    defaultMessage: "Empty",
    description: "Battery state indicating sensor has no remaining power",
  },
});
export type DeviceId = string;
export type ReaderId = string;

export const DEVICE_TYPE_TO_NAME: Record<DeviceType, string> = {
  DEVICE_TYPE_UNSPECIFIED: "Unknown Device",
  SCOUT_V1: "Scout V1",
  SCOUT_V2: "Scout V2",
  CHORUS_RELAY: "Chorus Relay",
  BLUEFIN_RUUVI: "Explorer 1",
  SOFT_READER_APP: "Mobile Reader",
  BLUEROE: "R0 Tile",
  SCOUT_V3: "Scout V3",
  BLUEROE_1: "Seeker One",
  EXPLORER_2: "Explorer 2",
  EXPLORER_2XL: "Explorer 2XL",
  SEEKER_TESTING: "Seeker",
  EXPLORER_TESTING: "Explorer",
  EXPLORER_TILE: "Explorer Tile",
  EXPLORER_TILE_RNI: "Explorer Tile",
  SEEKER_MINI: "Seeker Mini",
  EXPLORER_TILE_HB: "Explorer Tile",
  EXPLORER_TILE_LILT: "Explorer Tile LiLT",
  HGD4: "HGD4",
  HGR4: "HGR4",
  EXPLORER_2XL_HB: "Explorer 2XL",
  EXPLORER_2_HB: "Explorer 2",
  SEEKER_ONE_XL_ODR: "Seeker One XL - ODR",
  SEEKER_MINI_HB: "Seeker Mini",
  SEEKER_ONE_HB: "Seeker One",
  EXPLORER_TILE_RNI_V2: "Explorer Tile v2",
  EXPLORER_TILE_LILT_HB: "Explorer Tile LiLT",
};

/**
 * Proto wire values for `Device.DeviceType` (`proto/proto/entity.proto`), keyed by `DeviceType`.
 * Wire order follows the proto enum, not the TypeScript union text order.
 */
export const DEVICE_TYPE_PROTO_NUMBER = {
  DEVICE_TYPE_UNSPECIFIED: 0,
  SCOUT_V1: 1,
  SCOUT_V2: 2,
  CHORUS_RELAY: 3,
  BLUEFIN_RUUVI: 4,
  SOFT_READER_APP: 5,
  BLUEROE: 6,
  SCOUT_V3: 7,
  BLUEROE_1: 8,
  EXPLORER_2: 9,
  EXPLORER_2XL: 10,
  SEEKER_TESTING: 11,
  EXPLORER_TESTING: 12,
  EXPLORER_TILE: 13,
  EXPLORER_TILE_RNI: 14,
  SEEKER_MINI: 15,
  EXPLORER_TILE_HB: 16,
  EXPLORER_TILE_LILT: 17,
  HGD4: 18,
  HGR4: 19,
  EXPLORER_2XL_HB: 20,
  EXPLORER_2_HB: 21,
  SEEKER_ONE_XL_ODR: 22,
  SEEKER_ONE_HB: 23,
  SEEKER_MINI_HB: 24,
  EXPLORER_TILE_RNI_V2: 25,
  EXPLORER_TILE_LILT_HB: 26,
} as const satisfies Record<DeviceType, number>;

export type SignalQuality =
  | "SIGNAL_QUALITY_UNSPECIFIED"
  | "EXCELLENT"
  | "GOOD"
  | "FAIR"
  | "POOR";

export type RssiSignalQuality =
  | "SIGNAL_QUALITY_UNSPECIFIED"
  | "EXCELLENT"
  | "GOOD"
  | "LOW";

export const signalQualityMessages: Record<SignalQuality, MessageDescriptor> =
  defineMessages({
    SIGNAL_QUALITY_UNSPECIFIED: {
      defaultMessage: "Unspecified",
      description:
        "LTE signal quality label indicating the quality is not specified",
    },
    EXCELLENT: {
      defaultMessage: "Excellent",
      description: "LTE signal quality label indicating excellent reception",
    },
    GOOD: {
      defaultMessage: "Good",
      description: "LTE signal quality label indicating good reception",
    },
    FAIR: {
      defaultMessage: "Fair",
      description: "LTE signal quality label indicating fair reception",
    },
    POOR: {
      defaultMessage: "Poor",
      description: "LTE signal quality label indicating poor reception",
    },
  });

export const rssiSignalQualityMessages: Record<
  RssiSignalQuality,
  MessageDescriptor
> = defineMessages({
  SIGNAL_QUALITY_UNSPECIFIED: {
    defaultMessage: "N/A",
    description:
      "RSSI signal quality label indicating signal quality is not applicable",
  },
  EXCELLENT: {
    defaultMessage: "Excellent",
    description: "RSSI signal quality label indicating excellent reception",
  },
  GOOD: {
    defaultMessage: "Good",
    description: "RSSI signal quality label indicating good reception",
  },
  LOW: {
    defaultMessage: "Low",
    description: "RSSI signal quality label indicating low reception",
  },
});

export type Device = {
  batterySocPercent: number | null;
  batteryState: keyof typeof BatteryState;
  batteryVoltMilli: 0;
  currentAssetIdentifiers: AssetId[];
  currentTripIdentifiers: TripId[];
  deviceId: DeviceId;
  categories: Category[];
  lastCheckInTime: string;
  lastLocation: Location;
  tamperInfo?: {
    tamperedTimestamp?: string;
  };
  temperatureCelsiusMilli: number;
  hasUnresolvedAlert: boolean;
  deviceType?: DeviceType;
  lteQualityInfo?: {
    rsrpDbm: number;
    signalQuality: SignalQuality;
  };
  /** Populated for standby / non-activated device listing. */
  provisionedOrgName?: string;
  provisionStartDate?: string;
  provisionEndDate?: string;
};

/** `ListStandbyDeviceIdsResponse`. */
export type StandbyDeviceIdsResponse = {
  deviceIds: string[];
  nextPageToken?: string;
};

/** `GetStandbyDeviceProvisioningSummaryResponse.DeviceTypeGroup`. */
export type StandbyProvisioningSummaryDeviceTypeGroup = {
  deviceType: string;
  deviceCount: number;
};

/**
 * `GetStandbyDeviceProvisioningSummaryResponse.DailyProvisioningSummaryCounts`.
 * `provisionedDate` is UTC midnight for that calendar day in the requested `timezone`.
 */
export type StandbyDailyProvisioningSummaryCounts = {
  provisionedDate: string;
  deviceTypeGroups: StandbyProvisioningSummaryDeviceTypeGroup[];
};

/** `GetStandbyDeviceProvisioningSummaryResponse`. */
export type StandbyDeviceProvisioningSummaryResponse = ApiPaginatedResponse & {
  summary: StandbyDailyProvisioningSummaryCounts[];
};

export type ReaderModel =
  | "READER_TYPE_UNSPECIFIED"
  | "ARUBA_WIFI_AP"
  | "CISCO_WIFI_AP"
  | "EXTREME_WIFI_AP"
  | "SCOUT_V2"
  | "SCOUT_V3"
  | "IOS_READER"
  | "ANDROID_READER"
  | "MERAKI_WIFI_AP"
  | "JUNIPER_WIFI_AP"
  | "WINDOWS_READER"
  | "HGD4"
  | "HGR4"
  | "READER_T101";

export type ReaderLocationType = "FIXED" | "DYNAMIC";

export type Reader = {
  readerId: string;
  readerType: ReaderModel;
  readerState: {
    firmwareString?: string;
    lastCheckInTime: string;
    batterySocPercent?: number | null;
    batteryState: keyof typeof BatteryState;
    readerLocation: Location;
  };
};

export const readerFactory = define<Reader>({
  readerId: () => faker.string.uuid(),
  readerType: "ARUBA_WIFI_AP" as ReaderModel,
  readerState: {
    firmwareString: faker.string.alphanumeric(10),
    lastCheckInTime: faker.date.recent().toISOString(),
    batterySocPercent: 0,
    batteryState: "EMPTY",
    readerLocation: locationFactory(),
  },
});

export type User = {
  mobileNumber: string;
  email: string;
  modificationTimestampNanos?: number | null;
  modificationUserId?: string;
  name: string;
  orgId?: string;
  orgName: string;
  role: UserRoleKey;
  status: UserStatusKey | "DELETED";
  userId: string;
  validUntilTimestampNanos?: number;
};

export const userFactory = (overrides: Partial<User> = {}): User => ({
  userId: "user-1",
  name: "John Doe",
  email: "john@example.com",
  mobileNumber: "+123456789",
  modificationTimestampNanos: null,
  modificationUserId: "admin",
  orgId: "org-1",
  orgName: "Example Org",
  role: "MANAGER",
  status: "ACTIVE",
  validUntilTimestampNanos: Date.now() * 1_000_000,
  ...overrides,
});

export const deviceFactory = define<Device>({
  batterySocPercent: 0,
  batteryState: "OK",
  batteryVoltMilli: 0,
  currentAssetIdentifiers: () => [],
  currentTripIdentifiers: () => [],
  deviceId: () => faker.string.uuid(),
  lastCheckInTime: faker.date.recent().toISOString(),
  lastLocation: () => locationFactory(),
  temperatureCelsiusMilli: faker.number.int(),
  hasUnresolvedAlert: false,
  categories: () => [],
  // lteQualityInfo omitted by default when not available on the device
});

export type DeviceLocation = {
  deviceId: DeviceId;
  location: Location;
  deviceType?: DeviceType;
};

export type Geofence = {
  eventRuleId: string;
  eventRuleName: string;
  placeName: string;
  center: Point;
  radiusMeters: number;
  formattedStreetAddress: string;
};

export type AnchorPointLocation = {
  latlng: Point;
  buildingId: string;
  floor: string;
  floorOffsetCm: number;
};

export type AnchorPoint = {
  name: string;
  location: AnchorPointLocation;
  anchorPointId: string;
  formattedStreetAddress: string;
  placeName: string;
};

export type GeoPointAndRadius = {
  point: Point;
  radiusMeters: number;
};

export enum CreationSource {
  CREATION_SOURCE_UNSPECIFIED,
  TRIP,
  ALERTS_SHEET,
  API,
}

export type Place = {
  placeId: string;
  orgId: string;
  ownerOrgName: string;
  customerName: string;
  streetAddress: string;
  formattedStreetAddress: string;
  pointAndRadius: GeoPointAndRadius;
  creationSource: CreationSource;
  creationTime: string;
  modificationUserId: string;
  modificationTime: string;
};

export const placeFactory = define<Place>({
  placeId: () => faker.string.sample(),
  orgId: () => faker.string.sample(),
  ownerOrgName: () => faker.string.sample(),
  customerName: () => faker.string.sample(),
  streetAddress: () => faker.string.sample(),
  formattedStreetAddress: () => faker.string.sample(),
  pointAndRadius: () => pointAndRadiusFactory(),
  creationSource: () =>
    faker.helpers.enumValue(CreationSource) as CreationSource,
  creationTime: () => faker.date.recent().toISOString(),
  modificationUserId: () => faker.string.uuid(),
  modificationTime: () => faker.date.recent().toISOString(),
});

export type GeofencesResponse = {
  geofences: Geofence[];
  nextPageToken?: string;
  prevPageToken?: string;
};

export type AnchorPointResponse = {
  anchorPoints: AnchorPoint[];
  nextPageToken?: string;
  prevPageToken?: string;
};

export type PlacesResponse = {
  places: Place[];
  nextPageToken?: string;
  prevPageToken?: string;
};

export interface CombinedItem {
  type: "GEO_FENCE" | "PLACE" | "ANCHOR_POINT";
  data: Geofence | Place | AnchorPoint;
}

export const mapFeatureBadgeMessage: Record<
  keyof typeof MapFeatureType,
  MessageDescriptor
> = defineMessages({
  ANCHOR_POINT: {
    defaultMessage: "Anchor Point",
    description: "Badge Indicating the Map Feature as Anchor Point",
  },
  GEO_FENCE: {
    defaultMessage: "Geofence",
    description: "Badge Indicating the Map Feature as Geofence",
  },
  PLACE: {
    defaultMessage: "Place",
    description: "Badge Indicating the Map Feature as Place",
  },
});

export type ListUsersResponse = {
  users: User[];
  nextPageToken?: string;
  prevPageToken?: string;
  totalSize: number;
};

export type CountUsersResponse = {
  totalActiveUsers: number;
  totalDeactivatedUsers: number;
  totalUsers: number;
};

export type SInt32Value = {
  value: number;
};

export type AggregatorDetails = {
  aggregatorDeviceId: string;
  aggregatorRecordTime: string;
  rxPowerDbm: number;
  txPowerDbm?: SInt32Value;
  aggregatorLocation?: Location;
  modificationTime?: string;
  deviceId?: string;
  recordTime?: string;
  deviceType?: DeviceType;
  aggregatorDeviceType?: DeviceType;
  uploadId?: string;
  startedStoppedMoving?: boolean;
  rsrpDbm?: number;
  signalQuality?: SignalQuality;
  rssiSignalQuality?: RssiSignalQuality;
  orgName?: string;
};

export const aggregatorFactory = define<AggregatorDetails>({
  aggregatorDeviceId: () => faker.string.uuid(),
  aggregatorRecordTime: () => faker.date.recent().toISOString(),
  rxPowerDbm: () => faker.number.int({ min: -100, max: -30 }),
  txPowerDbm: () => ({
    value: faker.number.int({ min: -100, max: -30 }),
  }),
  aggregatorLocation: () => locationFactory(),
  modificationTime: () => faker.date.recent().toISOString(),
  deviceId: () => faker.string.uuid(),
  recordTime: () => faker.date.recent().toISOString(),
  deviceType: "SCOUT_V2",
  aggregatorDeviceType: "SCOUT_V2",
  uploadId: () => faker.string.uuid(),
  startedStoppedMoving: () => faker.datatype.boolean(),
  rsrpDbm: () => faker.number.int({ min: -140, max: -70 }),
  signalQuality: "GOOD",
  orgName: "Example Org",
});

export type ListSensorsSeenByReaderRequest = {
  pageSize?: number;
  pageToken?: string;
  searchString?: string;
  seenAfterTime?: string;
  seenBeforeTime?: string;
  sensorTypes?: DeviceType[];
};

export type ListSensorsSeenByReaderResponse = {
  sensorMeasures: AggregatorDetails[];
  prevPageToken?: string;
  nextPageToken?: string;
  totalSize: number;
};

export type AggregatorSeenByRecord = {
  readerId: string;
  readerType: ReaderModel;
  seenTime: string;
  rxPowerDbm: number;
  rssiSignalQuality: RssiSignalQuality;
};

export type ListSeenByHistoryForDeviceRequest = {
  pageSize?: number;
  pageToken?: string;
  startTime?: string;
  endTime?: string;
  readerType?: ReaderModel[];
};

export type ListSeenByHistoryForDeviceResponse = {
  records: AggregatorSeenByRecord[];
  prevPageToken?: string;
  nextPageToken?: string;
  totalSize: number;
};
